import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../../lib/api';

// Screen Capture & Export Protection (4 Nov 2026): a personal watermark on every
// screen, for every user including Super Admin — no setting to turn it off. Readable
// in a photo but not blocking work; blocks nothing itself (that's Part C2-C4).
export function Watermark() {
  const [mark, setMark] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const containerRef = useRef(null);
  const [host, setHost] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchMark = () => {
      api.get('/auth/session-mark').then((res) => {
        if (!cancelled) setMark(res.data);
      }).catch(() => {});
    };
    fetchMark();
    const interval = setInterval(fetchMark, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(tick);
  }, []);

  // The overlay must not be removable by a normal re-render; re-mount it into the
  // body if something ever detaches it from the DOM.
  useEffect(() => {
    const hostEl = document.createElement('div');
    hostEl.setAttribute('data-watermark-host', 'true');
    document.body.appendChild(hostEl);
    setHost(hostEl);
    const observer = new MutationObserver(() => {
      if (!document.body.contains(hostEl)) {
        document.body.appendChild(hostEl);
      }
    });
    observer.observe(document.body, { childList: true });
    return () => { observer.disconnect(); hostEl.remove(); };
  }, []);

  if (!mark) return null;

  const dateTime = now.toLocaleString();
  const text = `${mark.name} · ${mark.email} · ${dateTime} · ${mark.ip}`;
  const tiles = Array.from({ length: 48 });

  const content = (
    <div
      ref={containerRef}
      aria-hidden="true"
      data-testid="screen-watermark"
      style={{
        position: 'fixed', inset: 0, zIndex: 2147483000, pointerEvents: 'none',
        overflow: 'hidden', display: 'flex', flexWrap: 'wrap', alignContent: 'space-around',
        justifyContent: 'space-around', transform: 'rotate(-28deg) scale(1.4)',
      }}
      className="select-none"
    >
      {tiles.map((_, i) => (
        <span
          key={i}
          className="text-foreground dark:text-foreground whitespace-nowrap"
          style={{ fontSize: '12px', opacity: 'var(--watermark-opacity, 0.08)', margin: '28px 36px' }}
        >
          {text}
        </span>
      ))}
    </div>
  );

  if (!host) return null;
  // Render via portal so it always lives at document.body, above dialogs/sheets.
  return createPortal(content, host);
}

export default Watermark;
